import { useMemo } from "react"

import { Trans, useTranslation } from "@l2r-front/l2r-i18n"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { Typography } from "@l2r-front/l2r-ui"

import { RoadAssesmentList } from "../../../../common/components/RoadAssesmentList"
import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { DAMAGES_URL_PREFIX_PARAM } from "../../../../common/constants/urlParams"
import { DamagesStat } from "../../components/DamagesStat"
import { damagesCauseConfig } from "../../constants/damagesCauseConfig"
import { useRoadwayDispatchContext, useRoadwayStateContext } from "../../contexts/RoadwayContext"
import { useNetworkConditionForYearlyStatus } from "../../hooks/queries/networkConditions/useNetworkConditionForYearlyStatus"

import * as Styled from "./DamagesList.styled"

export function DamagesList(props) {

    const {
        damagesToDisplay,
        isLoading,
        isError,
        noDamageTextKey,
    } = props

    const {
        networkGradings,
        selectedNetworkYearlyStatus,
        selectedNetworkAssesmentType,
    } = useRoadwayStateContext()

    const {
        isLoading: isLoadingNetworkCondition,
        isError: isErrorNetworkCondition,
        networkCondition,
    } = useNetworkConditionForYearlyStatus(selectedNetworkYearlyStatus)

    const { setSelectedNetworkAssesmentType } = useRoadwayDispatchContext()
    const { t } = useTranslation(I18N_NAMESPACE)

    const filteredDamagesList = useMemo(() => {
        return (networkCondition?.damageTypes || []).filter(damageType => !damagesToDisplay || (damagesToDisplay || []).some(damageToDisplay => {
            return damageToDisplay.damageCode === damageType.code
        })).map(damageType => {
            const damage = (damagesToDisplay || []).find(damageToDisplay => {
                return damageToDisplay.damageCode === damageType.code
            })
            return ({
                ...damageType,
                damageStat: damage?.damageStat,
            })
        })
            .sort((a, b) => a.name.localeCompare(b.name))
    }, [networkCondition?.damageTypes, damagesToDisplay])

    const damagesItems = useMemo(() => {
        if (isLoading || isLoadingNetworkCondition) {
            return [<Styled.RoadInfoItemSkeleton key="roadInfo-loading" />]
        }

        if (isError || isErrorNetworkCondition) {
            return [<Styled.ErrorText key="roadInfo-error">{t(I18N_NAMESPACE, "containers.roadwaySidebar.damages.error")}</Styled.ErrorText>]
        }

        return filteredDamagesList.map(damageType => {
            return (
                <Styled.RoadInfoItem
                    endAdornments={<DamagesStat damageType={damageType} />}
                    id={`damage-list-item-${damageType.code}`}
                    key={damageType.code}
                    label={damageType.name}
                    icon={damagesCauseConfig?.[damageType.cause].icon}
                    onClick={() => setSelectedNetworkAssesmentType(`${DAMAGES_URL_PREFIX_PARAM}${damageType.code}`)}
                    selected={selectedNetworkAssesmentType === `${DAMAGES_URL_PREFIX_PARAM}${damageType.code}`}
                >
                </Styled.RoadInfoItem>
            )
        })
    }, [filteredDamagesList,
        selectedNetworkAssesmentType,
        setSelectedNetworkAssesmentType,
        isLoading,
        isError,
        isLoadingNetworkCondition,
        isErrorNetworkCondition,
        t])

    const closestYearSurvey = useMemo(() => {
        if (networkGradings?.length) {
            return networkGradings.findLast(networkGrading => networkGrading.yearRelative === 0)?.year
        }
        return null
    }, [networkGradings])

    return <RoadAssesmentList listKey="damages" title={t(I18N_NAMESPACE, "containers.roadwaySidebar.damages.title")}>
        {
            damagesItems.length ? damagesItems :
                selectedNetworkYearlyStatus?.isSimulated ? (
                    <Typography id="no-damage-warning">
                        <Trans namespace={I18N_NAMESPACE} i18nKey="containers.roadwaySidebar.damages.noDamageForSimulation">
                            "Ageing laws can be used to calculate the statistical evolution of pavment damages for rating purposes. The geographical position of the damages can be displayed on the date of a ground survey. To see them, select the year of the last ground survey (<strong>{{ year: closestYearSurvey }}</strong>). "
                        </Trans>
                    </Typography>
                ) : (
                    <Typography id="no-damage-warning">
                        {t(I18N_NAMESPACE, noDamageTextKey)}
                    </Typography>
                )
        }
    </RoadAssesmentList>
}

DamagesList.propTypes = {
    damagesToDisplay: PropTypes.arrayOf(PropTypes.shape({
        damageCode: PropTypes.string.isRequired,
        damageStat: PropTypes.number.isRequired,
    })),
    isSegment: PropTypes.bool,
    isError: PropTypes.bool,
    isLoading: PropTypes.bool,
    noDamageTextKey: PropTypes.string.isRequired,
}

DamagesList.defaultProps = {
    damagesToDisplay: [],
}